import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, VStack, useBreakpointValue,
} from '@chakra-ui/react';
import { FaArrowRight, FaBolt } from 'react-icons/fa';
import { brl, proximoAumentoEm, valor } from './regras';

function Linha({ rotulo, de, para, destaque = false, cor }) {
    return (
        <Flex justify="space-between" align="center" gap={3} py={2} borderBottomWidth="1px" borderColor="gray.100">
            <Text fontSize="sm" color="gray.600">{rotulo}</Text>
            <HStack spacing={2} flexShrink={0}>
                <Text fontSize="sm" color="gray.500" textDecoration="line-through">{de}</Text>
                <Icon as={FaArrowRight} boxSize={2.5} color="gray.400" />
                {destaque
                    ? <Badge colorScheme={cor} fontSize="sm" px={2} py={0.5} borderRadius="md">{para}</Badge>
                    : <Text fontSize="sm" fontWeight="bold">{para}</Text>}
            </HStack>
        </Flex>
    );
}

/**
 * Confirmacao do "Aumentar agora".
 *
 * Dinheiro que sai na hora e nao volta: antes do toque o operador ve a
 * gorjeta de agora e a nova, quanto a corrida passa a custar, como fica a
 * margem e quando vem o aumento seguinte. Recusa da Lalamove aparece aqui
 * mesmo, sem fechar, para o operador decidir o que fazer.
 */
export default function ConfirmarAumento({ isOpen, onClose, ordem, onConfirmar }) {
    const compacto = useBreakpointValue({ base: true, sm: false });
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(null);
    // Foco no Cancelar: Enter sem querer nao gasta dinheiro.
    const cancelarRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setErro(null);
            setEnviando(false);
        }
    }, [isOpen]);

    const g = ordem?.gorjeta;
    if (!g || g.proximo_valor == null) return null;

    const diferenca = Math.round((g.proximo_valor - g.atual) * 100) / 100;
    const custoAntes = g.valor_original + g.atual;
    const custoDepois = g.valor_original + g.proximo_valor;
    const temMargem = ordem.margem !== null && ordem.margem !== undefined && ordem.frete_cobrado != null;
    const margemDepois = temMargem ? Math.round((ordem.margem - diferenca) * 100) / 100 : null;

    // Depois do toque a linha do tempo anda ate o degrau novo; o aumento
    // seguinte e o proximo degrau que muda o valor (mesma conta do servidor).
    let seguinte = null;
    let minutosAteSeguinte = null;
    if (g.regra) {
        const s = Math.max(0, (Date.now() - new Date(ordem.criada_em).getTime()) / 1000 + (g.adiantado_seg || 0));
        const novoMarco = proximoAumentoEm(g.regra, s, g.valor_original);
        const depois = novoMarco === null ? null : proximoAumentoEm(g.regra, novoMarco, g.valor_original);
        if (depois !== null) {
            seguinte = valor(g.valor_original, g.regra, depois);
            minutosAteSeguinte = Math.round((depois - novoMarco) / 60);
        }
    }

    const confirmar = async () => {
        setEnviando(true);
        setErro(null);
        const res = await onConfirmar();
        setEnviando(false);
        if (res?.ok) onClose();
        else setErro(res?.erro || 'Não foi possível aumentar a gorjeta agora.');
    };

    const sinal = (v) => `${v >= 0 ? '+' : ''}${brl(v)}`;

    return (
        <Modal
            isOpen={isOpen}
            onClose={enviando ? () => {} : onClose}
            isCentered={!compacto}
            motionPreset={compacto ? 'slideInBottom' : 'scale'}
            closeOnOverlayClick={!enviando}
            initialFocusRef={cancelarRef}
        >
            <ModalOverlay />
            <ModalContent
                mx={compacto ? 0 : 4}
                mb={compacto ? 0 : undefined}
                mt={compacto ? 'auto' : undefined}
                borderBottomRadius={compacto ? 0 : 'md'}
                borderTopRadius="xl"
                maxW={compacto ? '100%' : 'md'}
                containerProps={compacto ? { alignItems: 'flex-end' } : undefined}
                pb={compacto ? 'env(safe-area-inset-bottom, 0px)' : undefined}
            >
                <ModalHeader fontSize="md" pb={1} pr={12}>
                    <HStack spacing={2}>
                        <Icon as={FaBolt} color="orange.500" />
                        <Text>Aumentar a gorjeta agora?</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton isDisabled={enviando} />
                <ModalBody>
                    <VStack align="stretch" spacing={3}>
                        <Box textAlign="center" bg="orange.50" borderRadius="lg" py={3}>
                            <Text fontSize="xs" color="orange.700" fontWeight="bold" textTransform="uppercase">Gorjeta nova</Text>
                            <Text fontSize="3xl" fontWeight="bold" color="orange.700" lineHeight="1.1">{brl(g.proximo_valor)}</Text>
                            <Text fontSize="sm" color="orange.700">+{brl(diferenca)} agora, sem esperar o intervalo</Text>
                        </Box>

                        <Box>
                            <Linha rotulo="Gorjeta" de={brl(g.atual)} para={brl(g.proximo_valor)} />
                            <Linha rotulo="Custo da corrida" de={brl(custoAntes)} para={brl(custoDepois)} />
                            {temMargem && (
                                <Linha
                                    rotulo="Margem"
                                    de={sinal(ordem.margem)}
                                    para={sinal(margemDepois)}
                                    destaque
                                    cor={margemDepois >= 0 ? 'green' : 'red'}
                                />
                            )}
                        </Box>

                        <Text fontSize="xs" color="gray.600">
                            {seguinte
                                ? `Se ninguém aceitar, o próximo aumento vem em ${minutosAteSeguinte} min, para ${brl(seguinte)}. Teto: ${brl(g.teto)}.`
                                : `Com este aumento a gorjeta chega no teto de ${brl(g.teto)} e não sobe mais.`}
                        </Text>

                        <Alert status="warning" borderRadius="md" fontSize="xs" py={2}>
                            <AlertIcon boxSize={4} />
                            <AlertDescription>A Lalamove cobra na hora e não deixa baixar a gorjeta depois.</AlertDescription>
                        </Alert>

                        {erro && (
                            <Alert status="error" borderRadius="md" fontSize="sm" py={2}>
                                <AlertIcon boxSize={4} />
                                <AlertDescription>{erro}</AlertDescription>
                            </Alert>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2} flexDirection={compacto ? 'column-reverse' : 'row'}>
                    <Button ref={cancelarRef} variant="ghost" onClick={onClose} isDisabled={enviando} w={compacto ? '100%' : 'auto'} minH="44px">
                        Cancelar
                    </Button>
                    <Button
                        colorScheme="orange"
                        onClick={confirmar}
                        isLoading={enviando}
                        loadingText="Aumentando"
                        w={compacto ? '100%' : 'auto'}
                        minH="44px"
                    >
                        Aumentar para {brl(g.proximo_valor)}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
